import React, { useState, useRef, useEffect, useCallback } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from '../../../../services/notificationService';
import { useCreateApplication, useUpdateApplication } from 'hooks/api';
import type { CreateApplicationRequest, UpdateApplicationRequest, ApplicationResponse } from 'api/types';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

// ============================================================================
// TYPES
// ============================================================================

interface ApplicationFormModalProps {
  isOpen: boolean;
  mode?: 'create' | 'edit';
  application?: ApplicationResponse | null;
  onClose: () => void;
  onSaveSuccess: () => void;
  tabId?: string;
  moduleName?: string;
  onMinimize?: () => void;
  onRestoreModal?: (restoredMode: 'create' | 'edit', instanceId?: string, appCode?: string) => void;
  createInstanceId?: string;
}

interface ApplicationFormData {
  appName: string;
  appCode: string;
  appVersion: string;
  description: string;
  callbackUrl: string;
  logoutUrl: string;
  keycloakRealmType: string;
  keyCloakRealmName: string;
  clientSecret: string;
  metadata: string;
}

const INITIAL_FORM_DATA: ApplicationFormData = {
  appName: '',
  appCode: '',
  appVersion: '',
  description: '',
  callbackUrl: '',
  logoutUrl: '',
  keycloakRealmType: 'shared',
  keyCloakRealmName: '',
  clientSecret: '',
  metadata: '',
};

// ============================================================================
// COMPONENT
// ============================================================================

const ApplicationFormModal: React.FC<ApplicationFormModalProps> = ({
  isOpen,
  mode = 'create',
  application,
  onClose,
  onSaveSuccess,
  tabId,
  moduleName,
  onMinimize,
  onRestoreModal,
  createInstanceId,
}) => {
  const isEdit = mode === 'edit';
  const { createApplication, loading: creating } = useCreateApplication();
  const { updateApplication, loading: updating } = useUpdateApplication();
  const saving = creating || updating;
  const { minimize } = useModalMinimize();
  const formDataRef = useRef<ApplicationFormData>(INITIAL_FORM_DATA);
  const isRestoreRef = useRef(false);

  const [formData, setFormData] = useState<ApplicationFormData>(INITIAL_FORM_DATA);
  formDataRef.current = formData;
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const initialSnapshotRef = useRef('');

  // Reset form on open — populate from application in edit mode
  useEffect(() => {
    if (isOpen) {
      if (isRestoreRef.current) {
        isRestoreRef.current = false;
        setFieldErrors({});
        return;
      }
      if (isEdit && application) {
        const editData: ApplicationFormData = {
          appName: application.appName || '',
          appCode: application.appCode || '',
          appVersion: application.appVersion || '',
          description: application.description || '',
          callbackUrl: application.callbackUrl || '',
          logoutUrl: application.logoutUrl || '',
          keycloakRealmType: application.keycloakRealmType || '',
          keyCloakRealmName: application.keycloakRealmName || '',
          clientSecret: '',
          metadata: application.metadata || '',
        };
        setFormData(editData);
        initialSnapshotRef.current = JSON.stringify(editData);
      } else {
        setFormData({ ...INITIAL_FORM_DATA });
        initialSnapshotRef.current = JSON.stringify(INITIAL_FORM_DATA);
      }
      setFieldErrors({});
      setShowSecret(false);
    }
  }, [isOpen, isEdit, application]);

  const getFieldError = useCallback((field: string) => fieldErrors[field] || '', [fieldErrors]);

  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleFieldChange = useCallback((field: keyof ApplicationFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    clearFieldError(field);
  }, [clearFieldError]);

  // ---- Generate Secret ----
  const generateSecret = useCallback((length = 32): string => {
    const array = new Uint8Array(length);
    crypto.getRandomValues(array);
    return Array.from(array, b => b.toString(16).padStart(2, '0')).join('');
  }, []);

  const handleGenerateSecret = useCallback(() => {
    const secret = generateSecret();
    handleFieldChange('clientSecret', secret);
    setShowSecret(true);
  }, [generateSecret, handleFieldChange]);

  const handleCopySecret = useCallback(() => {
    if (!formData.clientSecret) return;
    navigator.clipboard.writeText(formData.clientSecret).then(() => {
      notificationService.toast.success('Client secret copied to clipboard');
    });
  }, [formData.clientSecret]);

  // ---- Validation ----
  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.appName.trim()) errors.appName = 'Application name is required';
    else if (formData.appName.length < 2) errors.appName = 'Minimum 2 characters';
    else if (formData.appName.length > 100) errors.appName = 'Maximum 100 characters';

    if (!isEdit) {
      if (!formData.appCode.trim()) errors.appCode = 'Application code is required';
      else if (formData.appCode.length < 2) errors.appCode = 'Minimum 2 characters';
      else if (!/^[a-z0-9-]+$/.test(formData.appCode)) errors.appCode = 'Only lowercase letters, numbers, and hyphens';

      if (!formData.clientSecret.trim()) errors.clientSecret = 'Client secret is required';
      else if (formData.clientSecret.length < 8) errors.clientSecret = 'Minimum 8 characters';
      else if (formData.clientSecret.length > 500) errors.clientSecret = 'Maximum 500 characters';

      if (!formData.keycloakRealmType) errors.keycloakRealmType = 'Realm type is required';

      if (formData.keycloakRealmType === 'dedicated' && !formData.keyCloakRealmName.trim()) {
        errors.keyCloakRealmName = 'Realm name is required for dedicated realm type';
      }
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, isEdit]);

  // ---- Save ----
  const handleSaveClick = useCallback(() => {
    if (!validate()) return;
    setConfirmSaveOpen(true);
  }, [validate]);

  const handleConfirmSave = useCallback(async () => {
    setConfirmSaveOpen(false);

    try {
      if (isEdit && application) {
        const request: UpdateApplicationRequest = {
          appName: formData.appName.trim(),
          appVersion: formData.appVersion.trim() || undefined,
          description: formData.description.trim() || undefined,
          callbackUrl: formData.callbackUrl.trim() || undefined,
          logoutUrl: formData.logoutUrl.trim() || undefined,
          metadata: formData.metadata.trim() || undefined,
        };
        await updateApplication(application.appId, request, {
          onSuccess: () => {
            notificationService.toast.success(`Application "${formData.appName}" updated successfully.`);
            onSaveSuccess();
          },
        });
      } else {
        const request: CreateApplicationRequest = {
          appName: formData.appName.trim(),
          appCode: formData.appCode.trim(),
          appVersion: formData.appVersion.trim() || undefined,
          description: formData.description.trim() || undefined,
          callbackUrl: formData.callbackUrl.trim() || undefined,
          logoutUrl: formData.logoutUrl.trim() || undefined,
          keycloakRealmType: formData.keycloakRealmType as 'shared' | 'dedicated',
          keyCloakRealmName: formData.keyCloakRealmName.trim(),
          clientSecret: formData.clientSecret,
          metadata: formData.metadata.trim() || undefined,
        };
        await createApplication(request, {
          onSuccess: () => {
            notificationService.toast.success(`Application "${formData.appName}" created successfully.`);
            onSaveSuccess();
          },
        });
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || `Failed to ${isEdit ? 'update' : 'create'} application. Please try again.`;
      notificationService.toast.error(message);
    }
  }, [formData, isEdit, application, createApplication, updateApplication, onSaveSuccess]);

  // ---- Minimize ----
  const handleMinimize = useCallback(() => {
    const currentCreateInstanceId = createInstanceId;
    const savedMode = isEdit ? 'edit' as const : 'create' as const;
    const id = isEdit
      ? `${tabId || 'application'}-form-modal-edit-${application?.appCode || 'unknown'}`
      : `${tabId || 'application'}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    const title = formDataRef.current.appName
      ? `${isEdit ? 'Edit' : 'New'} App — ${formDataRef.current.appName}`
      : isEdit ? 'Edit Application' : `New Application (${currentCreateInstanceId || '1'})`;
    const savedFormData = { ...formDataRef.current };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot ? JSON.stringify(savedFormData) !== savedSnapshot : false;
    minimize({
      id,
      title,
      moduleName: moduleName || 'Platform Admin',
      tabId: tabId || 'application-master',
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) onRestoreModal(
          savedMode,
          savedMode === 'create' ? currentCreateInstanceId : undefined,
          savedMode === 'edit' ? application?.appCode : undefined,
        );
      },
    });
    if (onMinimize) {
      onMinimize();
    } else {
      onClose();
    }
  }, [tabId, moduleName, isEdit, application, minimize, onClose, onMinimize, onRestoreModal, createInstanceId]);

  // ---- Close with unsaved check ----
  const handleCloseAttempt = useCallback(() => {
    const dirty = JSON.stringify(formDataRef.current) !== initialSnapshotRef.current;
    if (dirty) {
      setConfirmDiscardOpen(true);
    } else {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={handleCloseAttempt} />
      <div className="modal-container modal-lg">
        {/* Loading overlay */}
        {saving && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
            <div style={{ width: '2.5rem', height: '2.5rem', border: '3px solid var(--border-color)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{isEdit ? 'Updating' : 'Creating'} application...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">
            <IconSystem.settings size={20} />
            {isEdit ? 'Edit Application' : 'Register New Application'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={saving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleCloseAttempt} disabled={saving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-content">
          {/* Row: App Name | App Code */}
          <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label className="form-label">Application Name *</label>
              <input
                type="text"
                className={`form-input ${getFieldError('appName') ? 'form-field-error' : ''}`}
                value={formData.appName}
                onChange={(e) => handleFieldChange('appName', e.target.value)}
                placeholder="Enter application name"
                maxLength={100}
              />
              {getFieldError('appName') && <div className="form-error-message">{getFieldError('appName')}</div>}
            </div>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label className="form-label">Application Code *</label>
              <input
                type="text"
                className={`form-input ${getFieldError('appCode') ? 'form-field-error' : ''}`}
                value={formData.appCode}
                onChange={(e) => handleFieldChange('appCode', e.target.value.toLowerCase())}
                placeholder="e.g. my-app"
                maxLength={50}
                disabled={isEdit}
                style={isEdit ? { background: 'var(--bg-secondary)', cursor: 'not-allowed' } : undefined}
              />
              {getFieldError('appCode') && <div className="form-error-message">{getFieldError('appCode')}</div>}
              {!isEdit && <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem' }}>Lowercase letters, numbers, hyphens only</div>}
            </div>
          </div>

          {/* Description */}
          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label className="form-label">Description</label>
            <textarea
              className="form-input"
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Brief description of the application"
              rows={2}
              maxLength={500}
              style={{ resize: 'vertical' }}
            />
          </div>

          {/* Row: Version | Client Secret (create) or Version | Status (edit) */}
          <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label className="form-label">Version</label>
              <input
                type="text"
                className="form-input"
                value={formData.appVersion}
                onChange={(e) => handleFieldChange('appVersion', e.target.value)}
                placeholder="e.g. 1.0.0"
                maxLength={20}
                autoComplete="off"
              />
            </div>
            {!isEdit && (
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
                  <label className="form-label" style={{ marginBottom: 0 }}>Client Secret *</label>
                  <div style={{ display: 'flex', gap: '0.25rem' }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleGenerateSecret}
                      title="Generate random secret"
                      style={{ padding: '0.15rem 0.35rem', lineHeight: 1, display: 'flex', alignItems: 'center' }}
                    >
                      <IconSystem.refresh size={14} />
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCopySecret}
                      title="Copy secret to clipboard"
                      disabled={!formData.clientSecret}
                      style={{ padding: '0.15rem 0.35rem', lineHeight: 1, display: 'flex', alignItems: 'center' }}
                    >
                      <IconSystem.copy size={14} />
                    </button>
                  </div>
                </div>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showSecret ? 'text' : 'password'}
                    className={`form-input ${getFieldError('clientSecret') ? 'form-field-error' : ''}`}
                    value={formData.clientSecret}
                    onChange={(e) => handleFieldChange('clientSecret', e.target.value)}
                    placeholder="Min 8 characters"
                    maxLength={500}
                    autoComplete="new-password"
                    style={{ paddingRight: '2.25rem' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecret(prev => !prev)}
                    title={showSecret ? 'Hide secret' : 'Show secret'}
                    style={{
                      position: 'absolute', right: '0.5rem', top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer', padding: '0.125rem',
                      color: 'var(--text-secondary)', display: 'flex', alignItems: 'center',
                    }}
                  >
                    {showSecret ? <IconSystem.eyeOff size={16} /> : <IconSystem.eye size={16} />}
                  </button>
                </div>
                {getFieldError('clientSecret') && <div className="form-error-message">{getFieldError('clientSecret')}</div>}
              </div>
            )}
            {isEdit && (
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">Status</label>
                <input
                  type="text"
                  className="form-input"
                  value={(application?.statusCode || '').toLowerCase().includes('active') ? 'Active' : (application?.statusName || '—').replace(/\s*application\s*/i, '')}
                  disabled
                  style={{ background: 'var(--bg-secondary)', cursor: 'not-allowed' }}
                />
              </div>
            )}
          </div>

          {/* Row: Realm Type | Realm Name */}
          <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              {isEdit ? (
                <>
                  <label className="form-label">Realm Type</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.keycloakRealmType === 'shared' ? 'Shared' : formData.keycloakRealmType === 'dedicated' ? 'Dedicated' : formData.keycloakRealmType || '—'}
                    disabled
                    style={{ background: 'var(--bg-secondary)', cursor: 'not-allowed' }}
                  />
                </>
              ) : (
                <SyutSelect
                  label="Realm Type *"
                  options={[
                    { value: 'shared', label: 'Shared' },
                    { value: 'dedicated', label: 'Dedicated' },
                  ]}
                  value={formData.keycloakRealmType}
                  onChange={(val: string | string[] | null) => handleFieldChange('keycloakRealmType', val || '')}
                  placeholder="Select realm type"
                  error={getFieldError('keycloakRealmType')}
                  isClearable={false}
                />
              )}
            </div>
            {formData.keycloakRealmType === 'dedicated' ? (
              <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
                <label className="form-label">{isEdit ? 'Realm Name' : 'Realm Name *'}</label>
                <input
                  type="text"
                  className={`form-input ${getFieldError('keyCloakRealmName') ? 'form-field-error' : ''}`}
                  value={formData.keyCloakRealmName}
                  onChange={(e) => handleFieldChange('keyCloakRealmName', e.target.value)}
                  placeholder="Enter realm name"
                  disabled={isEdit}
                  style={isEdit ? { background: 'var(--bg-secondary)', cursor: 'not-allowed' } : undefined}
                />
                {getFieldError('keyCloakRealmName') && <div className="form-error-message">{getFieldError('keyCloakRealmName')}</div>}
              </div>
            ) : <div style={{ flex: 1 }} />}
          </div>

          {/* Row: Callback URL | Logout URL */}
          <div style={{ display: 'flex', gap: '1.25rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label className="form-label">Callback URL</label>
              <input
                type="text"
                className="form-input"
                value={formData.callbackUrl}
                onChange={(e) => handleFieldChange('callbackUrl', e.target.value)}
                placeholder="https://example.com/callback"
              />
            </div>
            <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
              <label className="form-label">Logout URL</label>
              <input
                type="text"
                className="form-input"
                value={formData.logoutUrl}
                onChange={(e) => handleFieldChange('logoutUrl', e.target.value)}
                placeholder="https://example.com/logout"
              />
            </div>
          </div>

          {/* Metadata */}
          <div className="form-group">
            <label className="form-label">Metadata (JSON)</label>
            <textarea
              className="form-input"
              value={formData.metadata}
              onChange={(e) => handleFieldChange('metadata', e.target.value)}
              placeholder='Optional JSON metadata, e.g. {"key": "value"}'
              rows={2}
              style={{ resize: 'vertical' }}
            />
          </div>

          {/* Read-only info section — edit mode only */}
          {isEdit && application && (
            <div style={{
              marginTop: '0.5rem',
              padding: '0.75rem 1rem',
              background: 'var(--bg-secondary)',
              borderRadius: '6px',
              border: '1px solid var(--border-color)',
            }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                Application Info
              </div>
              <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', fontSize: '0.8rem' }}>
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>ID: </span>
                  <span style={{ color: 'var(--text-primary)', fontFamily: 'monospace', fontSize: '0.75rem' }}>{application.appId}</span>
                </div>
                <div>
                  <span style={{ color: 'var(--text-muted)' }}>Created: </span>
                  <span style={{ color: 'var(--text-primary)' }}>{new Date(application.createdAt).toLocaleString()}</span>
                </div>
                {application.updatedAt && (
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Last Updated: </span>
                    <span style={{ color: 'var(--text-primary)' }}>{new Date(application.updatedAt).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleCloseAttempt} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSaveClick} disabled={saving}>
            {isEdit ? 'Update Application' : 'Create Application'}
          </button>
        </div>
      </div>

      {/* Save Confirmation */}
      <ConfirmDialog
        open={confirmSaveOpen}
        title={isEdit ? 'Update Application' : 'Create Application'}
        message={isEdit
          ? `Save changes to application "${formData.appName}"?`
          : `Register application "${formData.appName}" with code "${formData.appCode}"?`
        }
        confirmLabel={isEdit ? 'Update' : 'Create'}
        cancelLabel="Cancel"
        onConfirm={handleConfirmSave}
        onCancel={() => setConfirmSaveOpen(false)}
      />

      {/* Discard Confirmation */}
      <ConfirmDialog
        open={confirmDiscardOpen}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setConfirmDiscardOpen(false); onClose(); }}
        onCancel={() => setConfirmDiscardOpen(false)}
      />
    </>
  );
};

export default ApplicationFormModal;
